/**
 * User Action Logger Diagnostic Tool
 * Captures and analyzes user action log payloads to identify validation issues
 */

import { UserActionLogDto } from '../types/logManagement';

export class UserActionLoggerDiagnostic {
  private static capturedPayloads: any[] = [];
  private static validationErrors: any[] = [];

  /**
   * Capture a user action payload before sending to backend
   */
  static capturePayload(payload: UserActionLogDto): void {
    const captured = {
      timestamp: new Date().toISOString(),
      payload: JSON.parse(JSON.stringify(payload)), // Deep clone
      payloadSize: JSON.stringify(payload).length,
      fieldCount: Object.keys(payload).length
    };

    this.capturedPayloads.push(captured);
    
    // Keep only last 10 payloads
    if (this.capturedPayloads.length > 10) {
      this.capturedPayloads.shift();
    }

    console.log('UserActionLoggerDiagnostic: Captured payload', captured);
  }

  /**
   * Capture validation error response
   */
  static captureValidationError(error: any, originalPayload?: UserActionLogDto): void {
    const captured = {
      timestamp: new Date().toISOString(),
      error: error,
      errorMessage: error?.message || 'Unknown error',
      errorResponse: error?.response?.data || null,
      statusCode: error?.response?.status || null,
      originalPayload: originalPayload ? JSON.parse(JSON.stringify(originalPayload)) : null
    };

    this.validationErrors.push(captured);
    
    // Keep only last 10 errors
    if (this.validationErrors.length > 10) {
      this.validationErrors.shift();
    }

    console.error('UserActionLoggerDiagnostic: Captured validation error', captured);
  }

  /**
   * Generate sample valid payload for testing
   */
  static generateSamplePayload(): UserActionLogDto {
    return {
      id: `user_action_${Date.now()}`,
      timestamp: new Date().toISOString(),
      level: 'info',
      message: 'User performed test action',
      source: 'UserActionLoggerDiagnostic',
      category: 'test',
      action: 'test_action',
      resourceType: 'test_resource',
      userId: 'test_user_123',
      sessionId: 'test_session_456',
      userAgent: navigator.userAgent,
      ipAddress: '127.0.0.1',
      tags: ['test', 'diagnostic'],
      metadata: {
        test: true,
        diagnostic: 'sample payload'
      }
    };
  }

  /**
   * Validate payload against expected structure
   */
  static validatePayload(payload: UserActionLogDto): {
    isValid: boolean;
    missingRequired: string[];
    invalidFields: string[];
    warnings: string[];
  } {
    const result = {
      isValid: true,
      missingRequired: [] as string[],
      invalidFields: [] as string[],
      warnings: [] as string[]
    };

    // Check required fields based on UserActionLogDto interface
    const requiredFields = ['id', 'timestamp', 'level', 'message', 'source', 'action', 'resourceType'];
    
    requiredFields.forEach(field => {
      if (!payload[field as keyof UserActionLogDto]) {
        result.missingRequired.push(field);
        result.isValid = false;
      }
    });

    // Check field types and formats
    if (payload.timestamp && !this.isValidISODate(payload.timestamp)) {
      result.invalidFields.push('timestamp (invalid ISO format)');
      result.isValid = false;
    }

    if (payload.level && !['error', 'warning', 'info', 'debug'].includes(payload.level)) {
      result.invalidFields.push('level (invalid value)');
      result.isValid = false;
    }

    // Check for potential issues
    if (payload.metadata && JSON.stringify(payload.metadata).length > 10000) {
      result.warnings.push('metadata is very large (>10KB)');
    }

    if (payload.tags && payload.tags.length > 20) {
      result.warnings.push('too many tags (>20)');
    }

    // Check for common field name variations that might cause issues
    if (payload.previousValue || payload.newValue) {
      result.warnings.push('Using previousValue/newValue - backend might expect oldValues/newValues');
    }

    return result;
  }

  /**
   * Analyze captured data to identify common issues
   */
  static analyzeIssues(): {
    commonMissingFields: string[];
    commonInvalidFields: string[];
    payloadSizeIssues: boolean;
    recommendations: string[];
  } {
    const analysis = {
      commonMissingFields: [] as string[],
      commonInvalidFields: [] as string[],
      payloadSizeIssues: false,
      recommendations: [] as string[]
    };

    // Analyze payload sizes
    if (this.capturedPayloads.length > 0) {
      const avgSize = this.capturedPayloads.reduce((sum, p) => sum + p.payloadSize, 0) / this.capturedPayloads.length;
      if (avgSize > 50000) { // 50KB
        analysis.payloadSizeIssues = true;
        analysis.recommendations.push('Payload size is large (>50KB). Consider reducing metadata size.');
      }
    }

    // Analyze validation errors
    this.validationErrors.forEach(error => {
      const errorMsg = (error.errorMessage || '').toLowerCase();
      
      if (errorMsg.includes('required') || errorMsg.includes('missing')) {
        analysis.recommendations.push('Check for missing required fields in UserActionLogDto');
      }
      
      if (errorMsg.includes('invalid') || errorMsg.includes('format')) {
        analysis.recommendations.push('Check field formats (dates, numbers, strings)');
      }
      
      if (errorMsg.includes('validation')) {
        analysis.recommendations.push('Backend validation rules may have changed');
      }

      if (errorMsg.includes('internal server error') || error.statusCode === 500) {
        analysis.recommendations.push('500 Internal Server Error - likely backend processing issue, not validation');
      }
    });

    return analysis;
  }

  /**
   * Get diagnostic report
   */
  static getDiagnosticReport(): {
    capturedPayloads: number;
    validationErrors: number;
    lastError: any;
    analysis: any;
    samplePayload: UserActionLogDto;
  } {
    return {
      capturedPayloads: this.capturedPayloads.length,
      validationErrors: this.validationErrors.length,
      lastError: this.validationErrors[this.validationErrors.length - 1] || null,
      analysis: this.analyzeIssues(),
      samplePayload: this.generateSamplePayload()
    };
  }

  /**
   * Clear all captured data
   */
  static clearData(): void {
    this.capturedPayloads = [];
    this.validationErrors = [];
    console.log('UserActionLoggerDiagnostic: Cleared all captured data');
  }

  /**
   * Export captured data for backend team analysis
   */
  static exportForBackendTeam(): string {
    const report = {
      generatedAt: new Date().toISOString(),
      endpoint: '/api/LogManagement/user-actions',
      summary: {
        totalPayloadsCaptured: this.capturedPayloads.length,
        totalValidationErrors: this.validationErrors.length,
        analysis: this.analyzeIssues()
      },
      sampleValidPayload: this.generateSamplePayload(),
      capturedPayloads: this.capturedPayloads,
      validationErrors: this.validationErrors,
      recommendations: [
        'Check backend processing for /api/LogManagement/user-actions endpoint',
        'Verify all required fields are properly handled',
        'Ensure field types match expected schema',
        'Test with the provided sample payload',
        'Check for any recent changes to UserActionLogDto validation',
        'If 500 Internal Server Error, check backend logs for processing errors'
      ]
    };

    return JSON.stringify(report, null, 2);
  }

  private static isValidISODate(dateString: string): boolean {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime()) && dateString.includes('T');
  }
}

export default UserActionLoggerDiagnostic;